/**
 * ToolSEOWrapper — Reusable SEO/AEO/GEO wrapper for all tool landing pages.
 * Adds: JSON-LD schema, medical disclaimers, author block, methodology, FAQs, internal links.
 * 
 * Usage: Wrap your tool page's main element with ToolSEOWrapper toolKey="key-name"
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, BookOpen, ArrowRight, FileCheck, ChevronDown } from 'lucide-react';
import { TOOL_SEO_DATA } from '../../lib/toolSEOData';

const SITE_URL = 'https://ninjathlete.com';

function injectJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('script'); el.id = id; el.type = 'application/ld+json'; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
  return el;
}

export function ToolSEOWrapper({ toolKey, children }) {
  const data = TOOL_SEO_DATA[toolKey];

  const meta = data?.meta;
  const schemaType = data?.schemaType;
  const medical = data?.medical;
  const peptide = data?.peptide;
  const sportsEvent = data?.sportsEvent;
  const intro = data?.intro;
  const methodology = data?.methodology;
  const faqs = data?.faqs;
  const internalLinks = data?.internalLinks;
  const toolUrl = `${SITE_URL}/tools/${toolKey}`;

  useEffect(() => {
    if (!data) return;
    // Set page title and meta description
    document.title = meta.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.name = 'description'; document.head.appendChild(metaDesc); }
    metaDesc.content = meta.description;

    const scripts = [];

    // 1. Primary schema (SoftwareApplication or MedicalWebPage)
    const primarySchema = {
      "@context": "https://schema.org",
      "@type": schemaType === 'MedicalWebPage' ? 'MedicalWebPage' : 'SoftwareApplication',
      "name": meta.h1,
      "url": toolUrl,
      "description": meta.description,
      "applicationCategory": "HealthApplication",
      "operatingSystem": "Web",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
      "author": { "@type": "Organization", "name": "NinjAthlete", "url": SITE_URL },
      ...(schemaType === 'MedicalWebPage' ? {
        "audience": { "@type": "MedicalAudience", "audienceType": "Researcher" },
        "specialty": "https://schema.org/Endocrine",
        "about": { "@type": "MedicalEntity", "name": "Research Peptides" },
        "disclaimer": "Educational and research use only. Not medical advice. Peptides referenced are research compounds not approved for human consumption. Consult a licensed healthcare provider before use.",
      } : {}),
      ...(sportsEvent ? {
        "about": { "@type": "SportsEvent", "name": "HYROX", "sport": "Functional Fitness Racing" },
      } : {}),
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["[data-speakable]", ".direct-answer", ".tool-intro"],
      },
    };
    scripts.push(injectJsonLd(`tool-schema-${toolKey}`, primarySchema));

    // 2. BreadcrumbList
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "Tools", "item": `${SITE_URL}/tools` },
        { "@type": "ListItem", "position": 3, "name": meta.h1, "item": toolUrl },
      ]
    };
    scripts.push(injectJsonLd(`tool-breadcrumb-${toolKey}`, breadcrumb));

    // 3. FAQPage schema
    if (faqs?.length) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({
          "@type": "Question",
          "name": f.question,
          "acceptedAnswer": { "@type": "Answer", "text": f.answer },
        })),
      };
      scripts.push(injectJsonLd(`tool-faq-${toolKey}`, faqSchema));
    }

    // 4. HowTo schema
    const howTo = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": `How to use the ${meta.h1}`,
      "description": intro,
      "step": [
        { "@type": "HowToStep", "text": "Enter your data into the calculator fields" },
        { "@type": "HowToStep", "text": "Review the methodology and formulas used" },
        { "@type": "HowToStep", "text": "Click calculate to see your personalized results" },
        { "@type": "HowToStep", "text": "Use the results to inform your training or research decisions" },
      ],
    };
    scripts.push(injectJsonLd(`tool-howto-${toolKey}`, howTo));

    return () => {
      scripts.forEach(s => { if (s?.parentNode) s.parentNode.removeChild(s); });
    };
  }, [toolKey, data, faqs, intro, meta, schemaType, sportsEvent, toolUrl]);

  return (
    <>
      {children}

      {/* SEO Content Section — renders below the tool UI */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        {/* Intro / Direct Answer Block */}
        {intro && (
          <div className="tool-intro mt-12 mb-8" data-speakable="true">
            <p className="text-zinc-400 text-sm leading-relaxed">{intro}</p>
          </div>
        )}

        {/* Medical Disclaimer (peptide tools only) */}
        {peptide && <PeptideDisclaimer />}

        {/* Methodology */}
        {methodology && <MethodologySection methodology={methodology} toolName={meta.h1} />}

        {/* FAQ Section */}
        {faqs?.length > 0 && <FAQSection faqs={faqs} />}

        {/* Internal Links */}
        {internalLinks?.length > 0 && <RelatedTools links={internalLinks} />}

        {/* Author Block */}
        <AuthorBlock peptide={peptide} />
      </div>
    </>
  );
}

function PeptideDisclaimer() {
  return (
    <div className="bg-amber-500/5 border border-amber-500/20 p-5 mb-8" data-testid="peptide-tool-disclaimer">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
        <div className="text-xs text-zinc-400 leading-relaxed">
          <p className="font-semibold text-amber-400 mb-2">Medical Disclaimer</p>
          <p className="mb-2">
            The information and calculations provided by this tool are for <strong className="text-zinc-300">educational and research purposes only</strong>. Peptides discussed on NinjAthlete are research compounds and are not FDA-approved for human consumption, treatment, cure, or prevention of any disease.
          </p>
          <p className="mb-2">
            This tool does not provide medical advice, diagnosis, or treatment recommendations. Dosing outputs are based on published research protocols and general pharmacological principles — they are not a prescription or a substitute for guidance from a licensed physician, pharmacist, or qualified healthcare provider.
          </p>
          <p>
            Always consult a licensed medical professional before using any peptide or research compound. NinjAthlete assumes no liability for decisions made based on the output of this tool.
            {' '}<Link to="/editorial-policy" className="text-amber-400 underline hover:text-white transition-colors">Read our editorial policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function MethodologySection({ methodology, toolName }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-zinc-800 mb-8">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-zinc-900/50 transition-colors"
        data-testid="methodology-toggle"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#E63946]" />
          <span className="text-white text-sm font-medium" style={{ fontFamily: 'Oswald, sans-serif' }}>
            How This Calculator Works
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-zinc-800 pt-3">
          <p className="text-zinc-400 text-xs leading-relaxed">{methodology}</p>
          <p className="text-zinc-600 text-[10px] mt-3">Last methodology review: April 2026 | Next scheduled review: October 2026</p>
        </div>
      )}
    </div>
  );
}

function FAQSection({ faqs }) {
  const [openIdx, setOpenIdx] = React.useState(0);
  return (
    <div className="mb-8" data-testid="tool-faq-section">
      <h2 className="text-white text-xl mb-4 uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
        Frequently Asked Questions
      </h2>
      <div className="space-y-2">
        {faqs.map((faq, i) => (
          <div key={faq.question} className="border border-zinc-800">
            <button
              onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-zinc-900/50 transition-colors"
            >
              <h3 className="text-zinc-200 text-sm font-medium pr-4" data-speakable="true">{faq.question}</h3>
              <ChevronDown className={`w-4 h-4 text-zinc-500 shrink-0 transition-transform ${openIdx === i ? 'rotate-180' : ''}`} />
            </button>
            {openIdx === i && (
              <div className="px-4 pb-4 border-t border-zinc-800 pt-3">
                <p className="text-zinc-400 text-sm leading-relaxed" data-speakable="true">{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RelatedTools({ links }) {
  return (
    <div className="mb-8">
      <h2 className="text-zinc-500 text-xs uppercase tracking-widest mb-3">Related Tools & Guides</h2>
      <div className="flex flex-wrap gap-2">
        {links.map(link => (
          <Link
            key={link.url}
            to={link.url}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs hover:border-[#E63946] hover:text-white transition-all"
          >
            {link.text} <ArrowRight className="w-3 h-3" />
          </Link>
        ))}
      </div>
    </div>
  );
}

function AuthorBlock({ peptide }) {
  return (
    <div className="border-t border-zinc-800 pt-6 mt-8">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E63946] to-[#1D3557] flex items-center justify-center text-white text-sm font-bold shrink-0">
          NA
        </div>
        <div>
          <p className="text-white text-sm font-medium mb-1">NinjAthlete Team</p>
          <p className="text-zinc-500 text-xs leading-relaxed mb-2">
            NinjAthlete is a performance optimization platform publishing evidence-based guides on HYROX training, peptide therapy, biohacking, and longevity science. HYROX content is informed by sub-60 competition experience (59:51, HYROX Dallas). Peptide content cites peer-reviewed research with DOI/PubMed links.
          </p>
          {peptide && (
            <p className="text-zinc-600 text-[10px] leading-relaxed mb-2">
              <strong className="text-zinc-500">Conflict of interest disclosure:</strong> NinjAthlete has affiliate relationships with peptide research suppliers. Discount codes may earn NinjAthlete a commission. This does not influence dosing methodology, research citations, or educational content.
            </p>
          )}
          <div className="flex items-center gap-3 text-[10px] text-zinc-600">
            <Link to="/editorial-policy" className="text-zinc-500 hover:text-white transition-colors underline">Editorial Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
